"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.addOrUpdateMapLayer = exports.addLayerAndMoveDrawingTools = exports.addOrUpdateMapLayerSource = exports.moveAllDrawingLayers = exports.getAllDrawingLayerIds = exports.makeOutlinePolygon = exports.wholeWorldBoundary = exports.featureIsMultiPolygon = exports.featureIsPolygon = exports.featureIsMultiPoint = exports.featureIsPoint = void 0;
const invariant_1 = require("@turf/invariant");
const turf_1 = require("@turf/turf");
const isEqual_1 = __importDefault(require("lodash/isEqual"));
const featureIsPoint = (feature) => {
    return (0, invariant_1.getType)(feature) === "Point";
};
exports.featureIsPoint = featureIsPoint;
const featureIsMultiPoint = (feature) => {
    return (0, invariant_1.getType)(feature) === "MultiPoint";
};
exports.featureIsMultiPoint = featureIsMultiPoint;
const featureIsPolygon = (feature) => {
    return (0, invariant_1.getType)(feature) === "Polygon";
};
exports.featureIsPolygon = featureIsPolygon;
const featureIsMultiPolygon = (feature) => {
    return (0, invariant_1.getType)(feature) === "MultiPolygon";
};
exports.featureIsMultiPolygon = featureIsMultiPolygon;
/**
 * This is the JSON for a polygon that covers the entire world.
 *
 * MultiPolygon to make typing easier.
 */
exports.wholeWorldBoundary = (0, turf_1.multiPolygon)([
    [
        [
            [0, 90],
            [180, 90],
            [180, -90],
            [0, -90],
            [-180, -90],
            [-180, 0],
            [-180, 90],
            [0, 90],
        ],
    ],
]);
/**
 * Take all the shapes we currently have and slice them out of the whole world.
 *
 * This lets us create a shader on the map everywhere _but_ the shape we're drawing.
 */
const makeOutlinePolygon = (shape) => {
    /**
     * If we got a feature collection, we pull out all the features,
     * otherwise, we wrap the one feature in an array.
     *
     * Then we filter anything that's not a polygon or multipolygon.
     * @todo Really look at the typing on the map layer, because it has no generics.
     * I'm pretty sure that it can be <Polygon | MultiPolygon>.
     */
    const individualShapes = ("features" in shape ? shape.features.map((feature) => feature) : [shape]).filter((feature) => feature.geometry.type === "Polygon" ||
        feature.geometry.type === "MultiPolygon");
    /**
     * Now that we have all the shapes we want to invert, we start with the whole world and start slicing our shapes out.
     */
    const wholeWorldMinusShape = individualShapes.reduce((acc, cur) => {
        const diff = (0, turf_1.difference)(acc, cur);
        if (!diff) {
            /**
             * This happens if we've cut the whole world out.
             * Extremely unlikely unless climate change is bad enough that people have started farming the ocean.
             * And the rest of the planet.
             * And someone put that all in one field in our system.
             * And our system supports _really big_ fields.
             * And Jeff Bezos has succeeded in buying the entire planet.
             *
             * In that case, make an empty polygon. I weep for you, future.
             */
            return (0, turf_1.multiPolygon)([[]]);
        }
        /**
         * Otherwise, we've sliced this bit out; let's move on to the next.
         */
        return diff;
    }, exports.wholeWorldBoundary);
    return wholeWorldMinusShape;
};
exports.makeOutlinePolygon = makeOutlinePolygon;
/**
 * The mapbox-gl-draw's MapboxDraw creates a bunch of layers on the map.
 * Each of them starts with `gl-draw`. This gathers all those ids.
 */
const getAllDrawingLayerIds = (map) => {
    const { layers } = map.getStyle();
    return ((layers === null || layers === void 0 ? void 0 : layers.filter((layer) => layer.id.startsWith("gl-draw")).map((layer) => layer.id)) || []);
};
exports.getAllDrawingLayerIds = getAllDrawingLayerIds;
/**
 * Send every drawing layer to the top of the stack.
 * This ensures that you're never drawing under a feature.
 */
const moveAllDrawingLayers = (map) => {
    const layerIds = (0, exports.getAllDrawingLayerIds)(map);
    layerIds.forEach((id) => map.moveLayer(id));
};
exports.moveAllDrawingLayers = moveAllDrawingLayers;
/**
 * Wrapper around Mapbox's addSource that will update a source instead if the source exists.
 * That means the map won't flash when a polygon changes.
 *
 * It also prevents crashes from React.StrictMode running everything twice in development.
 * Before this was here, strict mode would try to add a source that already existed, which would
 * crash the map.
 */
const addOrUpdateMapLayerSource = (id, data, map) => {
    const mapLayerSource = map.getSource(id);
    if (!mapLayerSource) {
        map.addSource(id, {
            type: "geojson",
            data,
        });
    }
    else {
        mapLayerSource.setData(data);
    }
};
exports.addOrUpdateMapLayerSource = addOrUpdateMapLayerSource;
/**
 * This function does what it says on the box: adds a layer to the map
 * and then makes sure all drawing tools are at the top of the stack.
 *
 * If in the future we want a layer to be able to be above the drawing tools,
 * we'll need to keep that placement in DrawingTools layers metadata and retrieve it here.
 */
const addLayerAndMoveDrawingTools = ({ layer, beforeId, map, }) => {
    map.addLayer(layer, beforeId);
    (0, exports.moveAllDrawingLayers)(map);
    return map;
};
exports.addLayerAndMoveDrawingTools = addLayerAndMoveDrawingTools;
/**
 * Wrapper around Mapbox's addLayer that will remove any layer that would get duplicated.
 * Also makes sure that all drawing tools are at the top of the map when a layer is added.
 *
 * Used to prevent crashes from React.StrictMode running everything twice in development.
 */
const addOrUpdateMapLayer = ({ layer, beforeId, map, }) => {
    const { id } = layer;
    const existingLayer = map.getLayer(id);
    if (!existingLayer) {
        /**
         * No existing layer? Then we don't have to do anything complicated.
         */
        return (0, exports.addLayerAndMoveDrawingTools)({ layer, beforeId, map });
    }
    else if (!(0, isEqual_1.default)(existingLayer, layer)) {
        /**
         * If there is an existing layer, we check to see if it matches the one
         * being passed in. If so, we don't have to do anything, but if not, we
         * remove the existing layer and add the new one.
         *
         * We _could_ manually update each property on the layer, but if `source`
         * changes, we'd have to remove and re-add anyway, and iterating over each
         * set function takes longer than re-rendering the layer.
         *
         * @todo Investigate if we could diff the objects and then see if we can just update
         * certain properties, like `paint`.
         */
        map.removeLayer(id);
        return (0, exports.addLayerAndMoveDrawingTools)({ layer, beforeId, map });
    }
    else if (beforeId) {
        /**
         * If we've gotten here, then there's an existing layer with an assigned `beforeId`.
         * We want to make sure it's in the right place.
         * We _could_ check the whole layer stack and see if it's in the right place,
         * but that's more computationally expensive than just moving it.
         */
        return map.moveLayer(id, beforeId);
    }
    return map;
};
exports.addOrUpdateMapLayer = addOrUpdateMapLayer;
